import { Icon } from "@mdi/react";
import { useCallback, useState } from "react";

import { FilterOutlined, PlusOutlined } from "@ant-design/icons";
import {
  DataEditor,
  DataEditorRef,
  GridCell,
  GridCellKind,
  GridSelection,
  Item
} from "@glideapps/glide-data-grid";
import { mdiMagnify } from "@mdi/js";
import { Button, Input, Popover } from "antd";
import { useAtom, useAtomValue } from "jotai";
import styled from "styled-components";

import { IconSpinner } from "components/icons";

import {
  ColumnFilterType,
  columnFiltersAtom,
  columnsAtom,
  filterCountAtom,
  filteredFormattedUwisAtom,
  filteredUwisAtom,
  gridColumnsAtom
} from "./atoms/fieldGroupAtoms";
import { GLIDE_GRID_THEME } from "./constants/glideGridTheme";
import { useColumnManagement } from "./hooks/useColumnManagement";
import { useColumnResize } from "./hooks/useColumnResize";
import { useEnrichColumnWithPythonScript } from "./hooks/useEnrichColumnWithPythonScript";
import { useGridCellEdit } from "./hooks/useGridCellEdit";
import { useGridKeyboard } from "./hooks/useGridKeyboard";
import { useGridPaste } from "./hooks/useGridPaste";

interface FieldGroupGlideGridProps {
  loading: boolean;
  duplicateColumnName?: string | null;
  gridRef: React.RefObject<DataEditorRef>;
  onRequestClose?: () => void;
}

export default function FieldGroupGlideGrid({
  loading,
  duplicateColumnName,
  gridRef,
  onRequestClose
}: FieldGroupGlideGridProps) {
  // Atoms
  const columns = useAtomValue(columnsAtom);
  const uwis = useAtomValue(filteredUwisAtom);
  const formattedUwis = useAtomValue(filteredFormattedUwisAtom);
  const gridColumns = useAtomValue(gridColumnsAtom);
  const filterCount = useAtomValue(filterCountAtom);
  const [columnFilters, setColumnFilters] = useAtom(columnFiltersAtom);

  const [selection, setSelection] = useState<GridSelection | undefined>();

  // Custom hooks
  const paste = useGridPaste();
  const resize = useColumnResize();
  const { enrichColumns } = useEnrichColumnWithPythonScript();

  // Enrich columns with pythonScript info from category list
  const enrichedColumns = enrichColumns(columns);
  const gridKeyboard = useGridKeyboard({
    selection,
    setSelection,
    onEscapeWhenIdle: onRequestClose
  });
  const columnManagement = useColumnManagement(gridRef);
  const cellEdit = useGridCellEdit();

  // Filter change handler
  const handleFilterChange = useCallback(
    (propertyName: string, filterType: ColumnFilterType) => {
      setColumnFilters((prev) => {
        const next = new Map(prev);
        if (filterType === ColumnFilterType.ShowAll) {
          // Remove filter when set to ShowAll
          next.delete(propertyName);
        } else {
          next.set(propertyName, filterType);
        }
        return next;
      });
    },
    [setColumnFilters]
  );

  // Get filter menu items for a column
  const getFilterMenuItems = useCallback(
    (propertyName: string) => {
      return [
        {
          key: ColumnFilterType.ShowAll,
          label: "Show All Rows",
          onClick: () => handleFilterChange(propertyName, ColumnFilterType.ShowAll)
        },
        {
          key: ColumnFilterType.HasValue,
          label: "Show Rows With Value",
          onClick: () => handleFilterChange(propertyName, ColumnFilterType.HasValue)
        },
        {
          key: ColumnFilterType.NoValue,
          label: "Show Rows Without Value",
          onClick: () => handleFilterChange(propertyName, ColumnFilterType.NoValue)
        }
      ];
    },
    [handleFilterChange]
  );

  // Filter menu state
  const [filterMenuState, setFilterMenuState] = useState<{
    visible: boolean;
    propertyName: string;
    x: number;
    y: number;
  }>({ visible: false, propertyName: "", x: 0, y: 0 });

  const hideFilterMenu = useCallback(() => {
    setFilterMenuState((prev) => ({ ...prev, visible: false }));
  }, []);

  // Grid cell content renderer
  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const uwi = uwis[row];
      if (!uwi) {
        return {
          kind: GridCellKind.Text,
          data: "",
          displayData: "",
          allowOverlay: true
        };
      }

      if (col === 0) {
        // UWI column - show formatted UWI for display ), keep raw for data
        const formattedUwi = formattedUwis[row] ?? uwi;
        return {
          kind: GridCellKind.Text,
          data: uwi,
          displayData: formattedUwi,
          allowOverlay: false,
          readonly: true,
          themeOverride: {
            bgCell: "#f5f5f5",
            textDark: "#999999"
          }
        };
      }

      const dataColIndex = col - 1;
      const column = enrichedColumns[dataColIndex];
      if (!column) {
        return {
          kind: GridCellKind.Text,
          data: "",
          displayData: "",
          allowOverlay: true
        };
      }

      const cellValue = column.data[uwi];
      const displayValue =
        cellValue !== undefined && cellValue !== null ? String(cellValue) : "";

      // Check if this is a Python-calculated column
      const isPythonColumn = column.pythonScript && column.pythonScript.trim() !== "";

      return {
        kind: GridCellKind.Text,
        data: displayValue,
        displayData: displayValue,
        readonly: isPythonColumn,
        themeOverride: isPythonColumn
          ? {
              bgCell: "rgba(0, 0, 0, 0.06)",
              textDark: "#595959"
            }
          : undefined,
        allowOverlay: !isPythonColumn
      };
    },
    [enrichedColumns, uwis, formattedUwis]
  );

  if (loading) {
    return (
      <LoadingContainer>
        <IconSpinner />
        <span>Loading field group data...</span>
      </LoadingContainer>
    );
  }

  const highlightedColumns = gridColumns.map((col) => {
    if (duplicateColumnName && col.id === duplicateColumnName) {
      return {
        ...col,
        themeOverride: {
          bgCell: "#fff1f0",
          bgHeader: "#ffccc7",
          bgHeaderHasFocus: "#ffa39e",
          bgHeaderHovered: "#ff7875",
          textHeader: "#cf1322"
        }
      };
    }
    return col;
  });

  return (
    <GridWrapper>
      <style>
        {`
          .add-column-popover .ant-popover-inner-content {
            width: 100% !important;
          }
        `}
      </style>
      <GridToolbar>
        {filterCount.isFiltered && (
          <FilterStatus>
            <FilterOutlined style={{ marginRight: 4 }} />
            Showing {filterCount.filtered.toLocaleString()} of{" "}
            {filterCount.total.toLocaleString()} rows
          </FilterStatus>
        )}
        <div style={{ flex: 1 }} />
        <SearchButton
          shape="circle"
          icon={<Icon path={mdiMagnify} size={1} />}
          onClick={() => gridKeyboard.setIsSearchVisible(!gridKeyboard.isSearchVisible)}
          title="Search"
        />
      </GridToolbar>
      <div
        style={{
          width: "100%",
          height: "100%",
          flex: 1,
          minHeight: 0,
          position: "relative"
        }}
        onKeyDown={gridKeyboard.handleGridKeyDown}>
        <DataEditor
          ref={gridRef}
          width="100%"
          height="100%"
          getCellContent={getCellContent}
          columns={highlightedColumns}
          rows={uwis.length}
          getCellsForSelection={true}
          theme={GLIDE_GRID_THEME}
          onFinishedEditing={() => {
            gridKeyboard.setIsEditing(false);
          }}
          onCellsEdited={cellEdit.onCellsEdited}
          onPaste={paste.onPaste}
          showSearch={gridKeyboard.isSearchVisible}
          onSearchClose={gridKeyboard.onSearchClose}
          onCellClicked={() => {
            gridKeyboard.setIsEditing(true);
          }}
          onHeaderClicked={paste.onHeaderClicked}
          onHeaderMenuClick={(col, bounds) => {
            const column = gridColumns[col];
            if (column && column.id && col > 0) {
              // col > 0 means it's not the UWI column
              const columnData = columns.find((c) => c.name === column.id);
              if (columnData?.isNew) {
                // Show delete menu for new columns
                columnManagement.showContextMenu(col, bounds.x, bounds.y + bounds.height);
              } else {
                // Show filter menu for all other columns
                setFilterMenuState({
                  visible: true,
                  propertyName: columnData?.propertyName || column.id,
                  x: bounds.x,
                  y: bounds.y + bounds.height
                });
              }
            }
          }}
          onColumnResize={resize.onColumnResize}
          gridSelection={selection}
          onGridSelectionChange={(newSelection) => {
            if (newSelection?.current === undefined) {
              gridKeyboard.setIsEditing(false);
            }
            setSelection(newSelection);
          }}
          rowMarkers="both"
          smoothScrollX
          smoothScrollY
          rightElement={
            <AddColumnContainer>
              <Popover
                content={
                  <PopoverContent>
                    <InputGroup>
                      <InputWrapper>
                        <InputLabel>Column Name</InputLabel>
                        <Input
                          ref={columnManagement.inputRef}
                          placeholder="Enter column name"
                          value={columnManagement.columnName}
                          onChange={columnManagement.handleColumnNameChange}
                          onPressEnter={columnManagement.handleKeyPress}
                          status={columnManagement.error ? "error" : ""}
                          autoFocus
                        />
                        {columnManagement.error && (
                          <ErrorText>{columnManagement.error}</ErrorText>
                        )}
                      </InputWrapper>
                      <InputWrapper>
                        <InputLabel>Units of Measure</InputLabel>
                        <Input
                          placeholder="UOM (optional)"
                          value={columnManagement.columnUnit}
                          onChange={columnManagement.handleColumnUnitChange}
                          onPressEnter={columnManagement.handleKeyPress}
                        />
                      </InputWrapper>
                    </InputGroup>
                    <PopoverButtons>
                      <Button size="small" onClick={columnManagement.handleCancel}>
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        onClick={columnManagement.handleAddColumn}
                        size="small">
                        Add
                      </Button>
                    </PopoverButtons>
                  </PopoverContent>
                }
                title="Add Column"
                trigger="click"
                open={columnManagement.isPopoverOpen}
                onOpenChange={columnManagement.handlePopoverOpenChange}
                placement="bottomRight"
                overlayStyle={{ width: "320px" }}
                overlayClassName="add-column-popover"
                zIndex={2000}>
                <Button type="default" icon={<PlusOutlined />} size="small" />
              </Popover>
            </AddColumnContainer>
          }
        />
        {columnManagement.menuColumn !== null && columnManagement.menuPosition && (
          <>
            <MenuOverlay onClick={columnManagement.hideContextMenu} />
            <MenuContainer
              style={{
                left: columnManagement.menuPosition.x,
                top: columnManagement.menuPosition.y
              }}>
              <MenuItem
                onClick={() => {
                  if (!columnManagement.menuColumn) return;
                  const column = gridColumns[columnManagement.menuColumn];

                  if (column?.id) {
                    columnManagement.handleRemoveColumn(column.id as string);
                  }
                }}>
                Remove
              </MenuItem>
            </MenuContainer>
          </>
        )}
        {filterMenuState.visible && (
          <>
            <MenuOverlay onClick={hideFilterMenu} />
            <FilterMenuContainer
              style={{
                left: filterMenuState.x,
                top: filterMenuState.y
              }}>
              <FilterMenuTitle>Filter Rows</FilterMenuTitle>
              {getFilterMenuItems(filterMenuState.propertyName).map((item) => (
                <FilterMenuItem
                  key={item.key}
                  onClick={() => {
                    item.onClick();
                    hideFilterMenu();
                  }}
                  $isActive={
                    (columnFilters.get(filterMenuState.propertyName) ||
                      ColumnFilterType.ShowAll) === item.key
                  }>
                  {item.label}
                </FilterMenuItem>
              ))}
            </FilterMenuContainer>
          </>
        )}
      </div>
    </GridWrapper>
  );
}

const GridWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
`;

const MenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
`;

const MenuContainer = styled.div`
  position: fixed;
  z-index: 1001;
  background: white;
  border-radius: 2px;
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);
  padding: 4px 0;
  min-width: 160px;
`;

const MenuItem = styled.div`
  padding: 5px 12px;
  cursor: pointer;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
  transition: all 0.2s;

  &:hover {
    background-color: #ff4d4f;
    color: white;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
  font-size: 16px;
  color: #666;
`;

const GridToolbar = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  height: 40px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 8px;
`;

const SearchButton = styled(Button)`
  color: #a2aaad;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;

  &:hover {
    color: var(--color-primary);
    background: transparent;
    border: none;
  }

  &:focus {
    background: transparent;
    border: none;
  }
`;

const AddColumnContainer = styled.div`
  display: flex;
  padding: 6px 8px;
  height: 100%;
`;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InputLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #595959;
  margin-bottom: 2px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  width: 100%;

  .ant-input {
    width: 100%;
  }
`;

const PopoverButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: #ff4d4f;
  word-wrap: break-word;
  white-space: normal;
`;

const FilterStatus = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: var(--color-primary);
  padding: 0 8px;
`;

const FilterMenuContainer = styled.div`
  position: fixed;
  z-index: 1001;
  background: white;
  border-radius: 4px;
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);
  padding: 4px 0;
  min-width: 200px;
`;

const FilterMenuTitle = styled.div`
  padding: 8px 12px 4px;
  font-size: 12px;
  color: #8c8c8c;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 4px;
`;

const FilterMenuItem = styled.div<{ $isActive?: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  color: ${(props) => (props.$isActive ? "var(--color-primary)" : "rgba(0, 0, 0, 0.85)")};
  background-color: ${(props) =>
    props.$isActive ? "rgba(var(--color-primary-rgb), 0.1)" : "transparent"};
  font-weight: ${(props) => (props.$isActive ? 600 : 400)};
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) =>
      props.$isActive ? "rgba(var(--color-primary-rgb), 0.2)" : "#f5f5f5"};
  }
`;
